import { useEffect, useState } from 'react';
import { Link } from 'react-router';
import { ApiError, apiFetch } from '../api/client.ts';
import { useAuth } from '../auth/AuthContext.tsx';
import { Button } from '../components/Button.tsx';
import { Card } from '../components/Card.tsx';

interface PreviewCandidate {
  contactId: string;
  email: string;
  name: string | null;
  organizationId: string;
  organizationName: string;
  isPortalUser: boolean;
}

interface PreviewResponse {
  candidateCount: number;
  emailReady: boolean;
  candidates: PreviewCandidate[];
}

interface SendOutcome {
  contactId: string;
  email: string;
  organizationName: string;
  emailSent: boolean;
  emailError: string | null;
  promotedToPortalUser: boolean;
}

interface SendResponse {
  candidateCount: number;
  attempted: number;
  sent: number;
  failed: number;
  promotedToPortalUser: number;
  ttlHours: number;
  outcomes: SendOutcome[];
}

export function AdminPortalInvitesPage(): React.ReactElement {
  const { agent } = useAuth();
  const [preview, setPreview] = useState<PreviewResponse | null>(null);
  const [previewError, setPreviewError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState<SendResponse | null>(null);
  const [resultError, setResultError] = useState<string | null>(null);
  const [confirming, setConfirming] = useState(false);

  async function loadPreview(): Promise<void> {
    setPreviewError(null);
    try {
      const r = await apiFetch<PreviewResponse>('/api/admin/portal-invites/preview');
      setPreview(r);
    } catch (e) {
      setPreviewError(e instanceof ApiError ? e.message : 'Failed to load preview.');
    }
  }

  useEffect(() => {
    void loadPreview();
  }, []);

  if (!agent) return <p>Loading…</p>;
  if (agent.role !== 'admin') {
    return (
      <Card>
        <p className="text-[var(--color-muted)]">Admins only.</p>
      </Card>
    );
  }

  async function send(): Promise<void> {
    setBusy(true);
    setResult(null);
    setResultError(null);
    try {
      const r = await apiFetch<SendResponse>('/api/admin/portal-invites/send-to-all', {
        method: 'POST',
        json: {},
      });
      setResult(r);
      setConfirming(false);
      void loadPreview();
    } catch (e) {
      setResultError(e instanceof ApiError ? e.message : 'Bulk send failed.');
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="mx-auto max-w-3xl space-y-6">
      <div>
        <Link to="/admin" className="text-sm text-[var(--color-muted)] hover:underline">
          ← Admin
        </Link>
        <h1 className="mt-1 text-2xl font-bold text-[var(--color-header)]">
          Send portal invites
        </h1>
        <p className="mt-1 text-sm text-[var(--color-muted)]">
          One-shot bulk send to every portal-flagged contact who hasn't completed enrollment.
          Each recipient gets a personal enrollment link valid for 7 days. Re-running is safe —
          already-enrolled contacts are skipped.
        </p>
      </div>

      {previewError && (
        <Card className="border-[var(--color-danger)]/40 bg-red-50/40">
          <p className="text-sm text-[var(--color-danger)]">{previewError}</p>
        </Card>
      )}

      {preview && (
        <Card>
          <div className="flex flex-wrap items-baseline justify-between gap-3">
            <h2 className="text-base font-semibold text-[var(--color-header)]">
              Will invite{' '}
              <span className="text-[var(--color-t3-blue)]">{preview.candidateCount}</span>{' '}
              contact{preview.candidateCount === 1 ? '' : 's'}
            </h2>
            {!preview.emailReady && (
              <span className="rounded-full bg-amber-100 px-2 py-0.5 text-xs font-medium text-amber-900">
                outbound email not connected
              </span>
            )}
          </div>
          <p className="mt-1 text-xs text-[var(--color-muted)]">
            Filter: every contact in an active organization with an email who hasn't completed
            TOTP enrollment. Contacts not yet flagged as portal users are automatically promoted
            when the invite goes out. Sent from the org's Gmail integration.
          </p>
          {preview.candidates.some((c) => !c.isPortalUser) && (
            <p className="mt-2 text-xs text-[var(--color-muted)]">
              <strong>
                {preview.candidates.filter((c) => !c.isPortalUser).length}
              </strong>{' '}
              of these will be promoted to portal users by this send.
            </p>
          )}

          {preview.candidates.length > 0 && (
            <details className="mt-3 text-sm">
              <summary className="cursor-pointer text-[var(--color-t3-blue)] hover:underline">
                Show the {preview.candidates.length} recipient
                {preview.candidates.length === 1 ? '' : 's'}
              </summary>
              <ul className="mt-2 max-h-72 list-disc space-y-1 overflow-y-auto pl-5 text-xs">
                {preview.candidates.map((c) => (
                  <li key={c.contactId}>
                    <strong>{c.email}</strong>
                    {c.name ? ` (${c.name})` : ''} —{' '}
                    <span className="text-[var(--color-muted)]">{c.organizationName}</span>
                  </li>
                ))}
              </ul>
            </details>
          )}

          {preview.candidateCount === 0 ? (
            <p className="mt-4 rounded-[var(--radius-button)] bg-[var(--color-surface)] px-3 py-2 text-sm text-[var(--color-muted)]">
              No unenrolled portal contacts. Nothing to do.
            </p>
          ) : !confirming ? (
            <div className="mt-4 flex flex-wrap items-center gap-2">
              <Button
                onClick={() => setConfirming(true)}
                disabled={busy || !preview.emailReady}
              >
                Send {preview.candidateCount} invite{preview.candidateCount === 1 ? '' : 's'}…
              </Button>
              {!preview.emailReady && (
                <span className="text-xs text-[var(--color-muted)]">
                  Connect Gmail outbound in{' '}
                  <Link to="/admin/google" className="text-[var(--color-t3-blue)] underline">
                    Admin → Team Google
                  </Link>{' '}
                  first.
                </span>
              )}
            </div>
          ) : (
            <div className="mt-4 rounded-[var(--radius-card)] border border-[var(--color-t3-blue)] bg-blue-50/40 p-3">
              <p className="text-sm text-[var(--color-header)]">
                This will send <strong>{preview.candidateCount}</strong> enrollment email
                {preview.candidateCount === 1 ? '' : 's'} right now. Each link is valid for{' '}
                <strong>7 days</strong>. Continue?
              </p>
              <div className="mt-3 flex gap-2">
                <Button onClick={() => void send()} disabled={busy}>
                  {busy ? 'Sending…' : 'Yes, send all'}
                </Button>
                <Button
                  variant="outline"
                  onClick={() => setConfirming(false)}
                  disabled={busy}
                >
                  Cancel
                </Button>
              </div>
            </div>
          )}
        </Card>
      )}

      {resultError && (
        <Card className="border-[var(--color-danger)]/40 bg-red-50/40">
          <p className="text-sm text-[var(--color-danger)]">{resultError}</p>
        </Card>
      )}

      {result && (
        <Card className="border-green-300 bg-green-50/40">
          <h2 className="text-base font-semibold text-green-900">Bulk send complete</h2>
          <ul className="mt-2 space-y-1 text-sm text-green-900">
            <li>Eligible contacts: {result.candidateCount}</li>
            <li>Attempted: {result.attempted}</li>
            <li>Sent: <strong>{result.sent}</strong></li>
            <li>
              Failed:{' '}
              <strong className={result.failed > 0 ? 'text-[var(--color-danger)]' : ''}>
                {result.failed}
              </strong>
            </li>
            <li>
              Promoted to portal users: <strong>{result.promotedToPortalUser}</strong>
            </li>
            <li>Each link valid for {result.ttlHours} hours.</li>
          </ul>
          {result.failed > 0 && (
            <details className="mt-3 text-sm" open>
              <summary className="cursor-pointer text-[var(--color-danger)]">
                {result.failed} failure{result.failed === 1 ? '' : 's'} (click to expand)
              </summary>
              <ul className="mt-2 max-h-72 list-disc space-y-1 overflow-y-auto pl-5 text-xs">
                {result.outcomes
                  .filter((o) => !o.emailSent)
                  .map((o) => (
                    <li key={o.contactId}>
                      <strong>{o.email}</strong> ({o.organizationName})
                      <div className="text-[var(--color-danger)]">
                        {o.emailError ?? 'unknown error'}
                      </div>
                    </li>
                  ))}
              </ul>
            </details>
          )}
        </Card>
      )}
    </div>
  );
}
